import React, { useEffect, useImperativeHandle, useState } from 'react';
import { browserName, isMobile, osName } from 'react-device-detect';
import moment from 'moment-timezone';
import update from 'immutability-helper';

import Appearance from 'styles/Appearance.js';
import { DocumentViewer, getLegalDocuments } from 'managers/Components';
import { LayerItem } from 'structure/Layer.js';
import LottieView from 'views/Lottie.js';
import Utils from 'files/Utils.js';

const LegalAgreements = React.forwardRef(({ documentIDs, editable = true, onComplete, lastItem = true, showDescription = false, title = 'Agreements', utils, value }, ref) => {
    
    const [loading, setLoading] = useState(false);
    const [location, setLocation] = useState(null);
    const [results, setResults] = useState({});

    const onDocumentClick = async document => {
        try {

            // request geolocaion information if not already available
            if(!location) {
                setLoading(document.id);
                await onRequestLocation();
                setLoading(false);
            }

            // open document viewer to review legal document
            utils.layer.open({
                Component: DocumentViewer.bind(this, { 
                    document,
                    onActionClick: val => {

                        // set acceptance value on legal agreements object and update local state
                        let next = update(results, {
                            [document.id]: {
                                $set: {
                                    date: val === true ? moment.utc().format('YYYY-MM-DD HH:mm:ss') : null,
                                    value: val
                                }
                            }
                        });
                        setResults(next);

                        // determine if a rejection was provided
                        if(val === false) {
                            utils.alert.show({
                                title: 'Just a Second',
                                message: `You must accept the ${document.title} to use this service. If you have any questions or concerns, please reach out to our support team for assistance.`
                            });
                        }

                        // determine if a completion handler is present
                        if(typeof(onComplete) === 'function') {
                            let payload = getCompletedAgreements(next);
                            if(payload) {
                                onComplete(payload);
                            }
                        }
                    }
                }),
                id: 'document_viewer'
            });
            
        } catch(e) {

            // show a specific location based alert if the user cancelled location request
            setLoading(false);
            if(e.message === 'user_cancelled') {
                utils.alert.show({
                    title: 'Just a Second',
                    message: `Your location is required for us to properly record your acceptance of the ${document.title}. Please ensure that location services are enabled in your browser settings and try again.`
                });
                return;
            }
            
            // silently fail and proceed if browser is unable to support geolocation services
            setLocation({ 
                metadata: {
                    browser_name: browserName, 
                    error: e.message,
                    is_mobile: isMobile, 
                    os: osName
                } 
            });
            setTimeout(onDocumentClick.bind(this, document), 1000);
        }
    }

    const onRequestLocation = () => {
        return new Promise(async (resolve, reject) => {

            // define function to request location from browser
            const requestLocation = () => {
                navigator.geolocation.getCurrentPosition(position => {
                    setLocation({
                        accuracy: position.coords.accuracy,
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    });
                    resolve();
                }, error => {
                    if(error.message.toLowerCase().includes('timeout') === false) {
                        reject(error);
                        return;
                    }
                    let e = new Error('Location request timed out. Please ensure location services are enabled and try again. If the issue persists, try another device or contact support for assistance.');
                    reject(e);
                }, {
                    enableHighAccuracy: true,
                    maximumAge: 0,
                    timeout: 10000
                });
            }

            // determine if geolocation permissions is already granted
            let status = await navigator.permissions.query({ name: 'geolocation' });
            if(status.state === 'granted') {
                requestLocation();
                return;
            }

            // show preface alert to prepare user for location request from browser
            utils.alert.show({
                title: 'Location Request',
                message: 'To comply with legal requirements, we need to record your location when you accept our agreements. Please allow location access in the next prompt.',
                buttons: [{
                    key: 'continue',
                    title: 'Continue',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    setTimeout(() => {
                        if(key === 'continue') {
                            requestLocation();
                            return;
                        }
                        let error = new Error('user_cancelled');
                        reject(error);
                    }, 500);
                }
            });
        });
    }

    const onSetInitialValues = () => {
        let payload = Object.keys(value).reduce((object, id) => {
            if(documentIDs.includes(id)) {
                object[id] = {
                    ...value[id],
                    value: true
                };
            }
            return object;
        }, {});
        setResults(payload);
    }

    const getCompletedAgreements = targets => {

        // prepare dates for acceptance
        let documents = Object.keys(targets).reduce((object, key) => {
            if(targets[key].value === true) {
                object[key] = {
                    date: targets[key].date,
                    document_id: key,
                    latitude: location?.latitude,
                    location_accuracy: location?.accuracy,
                    longitude: location?.longitude,
                    metadata: {
                        client_side_geolocation_failure: location?.client_side_geolocation_failure || false
                    }
                };
            }
            return object;
        }, {});
        
        // determine if all required documents have been reviewed and accepted
        if(Object.keys(documents).length === getLegalDocumentTargets().length) {
            return documents;
        }
    }

    const getValue = document => {

        // return a loader if location is currently being retrieved
        if(loading === document.id) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: 18,
                    width: 25
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 18,
                        width: 25
                    }}/>
                </div>
            )
        }

        // prepare label value for component
        let label = `${Utils.isMobile() ? 'Tap' : 'Click'} to review`;
        if(results[document.id]) {
            label = results[document.id].value ? 'Completed' : 'Rejected';
        }
        return (
            <span style={{
                ...Appearance.textStyles.value(),
                    flexGrow: 1,
                    ...results[document.id] && {
                        color: results[document.id].value ? Appearance.colors.green : Appearance.colors.red
                    }
            }}>{label}</span>
        )
    }

    const getLegalDocumentTargets = () => {
        return getLegalDocuments().filter(doc => documentIDs.includes(doc.id));
    }

    const getRefFunctions = () => {
        return { 
            getCompletedAgreements: async () => {
                return new Promise((resolve, reject) => {
                    try {

                        // loop through required documents and ensure they have been accepted
                        getLegalDocumentTargets().forEach(document => {
                            if(!results[document.id] || results[document.id].value === false) {
                                throw new Error(`Please review and complete the ${document.title} to continue.`);
                            }
                        });

                        // prepare completion payload and resolve
                        let payload = getCompletedAgreements(results);
                        resolve(payload);

                    } catch(e) {
                        reject(e);
                    }
                });
            } 
        };
    }

    useImperativeHandle(ref, getRefFunctions, [location, results]);

    useEffect(() => {
        value && onSetInitialValues();
    }, [value]);

    return (
        <LayerItem 
        collapsed={false}
        lastItem={lastItem}
        title={title}>
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                {showDescription === true && (
                     <span style={{
                        ...Appearance.textStyles.defaultText(),
                        borderBottom: Appearance.styles.bottomBorder(),
                        padding: '12px 16px',
                    }}>{`These agreements ensure transparency, legal compliance, and mutual trust between you and LifeWipe. By reviewing and accepting them, you’re confirming your understanding of how your digital assets are safeguarded and what rights and responsibilities you retain as a user.`}</span>
                )}
                {getLegalDocumentTargets().map((document, index, documents) => {
                    return (
                        <div 
                        className={editable ? `view-entry ${window.theme}` : ''}
                        key={index}
                        onClick={editable ? onDocumentClick.bind(this, document) : null}
                        style={{
                            alignItems: 'center',
                            borderBottom: index !== documents.length - 1 && Appearance.styles.bottomBorder(),
                            display: 'flex',
                            flexDirection: 'row',
                            padding: showDescription ? '12px 16px 12px 16px' : '8px 12px 8px 12px'
                        }}>
                            <span style={{
                                ...Appearance.textStyles.key(),
                                flexGrow: 1
                            }}>{document.title}</span>
                            {getValue(document)}
                            {editable === true && loading !== document.id && (
                                <img
                                src={'images/next-arrow-grey-small.png'}
                                style={{
                                    height: 12,
                                    objectFit: 'contain',
                                    marginLeft: 8,
                                    width: 12,
                                }} />
                            )}
                        </div>
                    )
                })}
            </div>
        </LayerItem>
    )
});

export default LegalAgreements;